import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';
import { Loader2 } from 'lucide-react';

const HERO_IMAGE = 'https://customer-assets.emergentagent.com/job_a3709e67-7648-4bcb-a123-219697b944ef/artifacts/aqwsfdgh_Martin_Aagaard_-_Kystlandskap_-_IMG_9313.jpg';

export default function LoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const { login, user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (user) {
      if (user.role === 'admin') {
        navigate('/admin');
      } else {
        navigate('/portal');
      }
    }
  }, [user, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const data = await login(email, password);
      toast.success('Welcome back');
      
      if (data.user.role === 'admin') {
        navigate('/admin');
      } else {
        navigate('/portal');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen relative" data-testid="login-page">
      {/* Background */}
      <div
        className="absolute inset-0 bg-center bg-cover opacity-30"
        style={{ backgroundImage: `url(${HERO_IMAGE})` }}
      />
      <div className="absolute inset-0 bg-[var(--cream)]/80" />

      {/* Content */}
      <div className="relative z-10 min-h-screen flex flex-col">
        {/* Header */}
        <header className="p-6">
          <Link to="/" className="text-lg text-[var(--navy-800)] font-light tracking-widest">
            COASTWISE TRADE
          </Link>
        </header>

        {/* Form */}
        <div className="flex-1 flex items-center justify-center px-6">
          <div className="w-full max-w-sm">
            <h1 className="text-2xl font-semibold text-[var(--navy-800)] text-center mb-8">
              Client Login
            </h1>
            
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-[var(--navy-800)] mb-2">Email</label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="input-field"
                  placeholder="you@example.com"
                  required
                  data-testid="login-email-input"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-[var(--navy-800)] mb-2">Password</label>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="input-field"
                  placeholder="••••••••"
                  required
                  data-testid="login-password-input"
                />
              </div>
              <button
                type="submit"
                className="btn-big btn-big-primary flex items-center justify-center"
                disabled={loading}
                data-testid="login-submit-button"
              >
                {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : 'Sign In'}
              </button>
            </form>

            <p className="mt-6 text-center text-sm text-[var(--muted-foreground)]">
              Don't have an account?{' '}
              <Link to="/invite" className="text-[var(--navy-800)] font-medium hover:underline">
                Enter invite code
              </Link>
            </p>
            <p className="mt-2 text-center text-sm text-[var(--muted-foreground)]">
              <Link to="/enter" className="text-[var(--navy-800)] font-medium hover:underline">
                ← Back
              </Link>
            </p>
            <p className="mt-6 text-center">
              <Link
                to="/admin/login"
                className="text-xs tracking-widest uppercase text-[var(--muted-foreground)] hover:text-[var(--navy-800)] transition-colors"
                data-testid="admin-access-link"
              >
                Staff Login
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
